import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useUser } from '../context/UserContext';
import { useAuthUI } from '../context/AuthUIContext';
import { Logo } from '../component/common/Logo';
import { CommonMessages } from '../i18n/Common.messages';
import { NavigationComponentMessages, NavigationComponentText } from './NavigationComponent.messages';
import { LocaleSelector } from './LocaleSelector';
import { localePrefixedPath, stripLocalePrefix } from '../component/seo/constants';
import { BillingMessages } from '../component/billing/BillingPage.messages';
import { useTheme } from '../context/ThemeContext';
import { signOut } from 'aws-amplify/auth';
import './Header.css';

export const Header: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();
    const user = useUser();
    const { openSignIn } = useAuthUI();
    const { theme, toggle: toggleTheme } = useTheme();

    /*
     * Whether we are past hydration.
     *
     * The theme toggle needs to report its state, and the state is only knowable on
     * the client: the pre-paint script in public/index.html resolves the theme before
     * React runs, but the prerendered HTML was captured in light mode. Reading `theme`
     * during the first render would therefore make that render disagree with the
     * served markup, which is the mismatch this header has always avoided by letting
     * CSS pick the icon from [data-theme]. Deferring to an effect keeps the first
     * render identical to the snapshot and adds aria-pressed immediately after.
     */
    const [hydrated, setHydrated] = useState(false);
    useEffect(() => { setHydrated(true); }, []);
    const [userMenuOpen, setUserMenuOpen] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const userRef = useRef<HTMLDivElement>(null);

    const isAuth = !!user.email;

    /*
     * Which nav item is highlighted.
     *
     * Was an exact `===` against location.pathname, which meant a section page never
     * lit its parent: /docs/cli did not match /docs, /integrations/react did not match
     * /integrations, and no article ever matched /blog. It also failed for every
     * locale, because /de/docs carries a prefix that no nav path has — so on a German
     * page nothing was highlighted at all.
     *
     * Strip the locale, then treat a path as active when it is the page or an ancestor
     * of it. The `/` guard matters: without it every path is "under" the root.
     */
    const currentPath = stripLocalePrefix(location.pathname);
    const isActive = (path: string) =>
        currentPath === path || (path !== '/' && currentPath.startsWith(`${path}/`))
            ? ' active'
            : '';

    /*
     * Nav links keep the visitor in the language they are reading: every `to` below
     * goes through localePrefixedPath, so someone on /de is not dropped back into
     * English mid-journey. Both URLs are prerendered, so this only decides which
     * language the next page speaks.
     *
     * `navigate` survives for the signed-in menu, whose destinations are app routes
     * rather than per-locale marketing pages.
     */

    // Close dropdowns on outside click
    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (userRef.current && !userRef.current.contains(e.target as Node)) setUserMenuOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    // Close mobile nav on route change
    useEffect(() => { setMobileOpen(false); }, [location.pathname]);

    /*
     * Escape closes the mobile nav. It exposed no state and could not be dismissed
     * from the keyboard at all: opening it with Enter worked, and then there was no
     * way out but to tab through every item.
     */
    useEffect(() => {
        if (!mobileOpen) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setMobileOpen(false);
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [mobileOpen]);

    const navLinks = [
        ...(isAuth
            ? [{ label: t(NavigationComponentMessages.NAV_DASHBOARD), path: '/dashboard' }]
            : []),
        { label: t(NavigationComponentMessages.NAV_PRICING), path: '/pricing' },
        { label: t(NavigationComponentMessages.NAV_DOCS), path: '/docs' },
        { label: t(NavigationComponentMessages.NAV_INTEGRATIONS), path: '/integrations' },
        { label: t(NavigationComponentMessages.NAV_MCP), path: '/mcp' },
        // /blog was reachable only by typing the URL, which left the articles out
        // of every crawl path. Last in the row so the conversion links keep their
        // position.
        {
            label: t(NavigationComponentMessages.NAV_BLOG, { defaultValue: NavigationComponentText.NAV_BLOG }),
            path: '/blog',
        },
    ];

    const initials = user.name ? user.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() : '?';

    return (
        <header className="site-header" id="app-header">
            <div className="container">
                <Link to={localePrefixedPath('/', location.pathname)} className="header-left">
                    <Logo size={28} />
                    <span className="header-brand">{t(CommonMessages.APP_NAME)}</span>
                </Link>

                {/* No need to close the language menu here: LocaleSelector closes itself
                    on any mousedown outside its trigger and its menu, and this is one. */}
                {!isAuth && <button
                    className="header-mobile-toggle"
                    onClick={() => setMobileOpen(!mobileOpen)}
                    aria-label="Menu"
                    aria-expanded={mobileOpen}
                    aria-controls="header-nav"
                >
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                        {mobileOpen ? <><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></> : <><line x1="3" y1="7" x2="21" y2="7"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="17" x2="21" y2="17"/></>}
                    </svg>
                </button>}

                {/*
                  Real links, not buttons.
                  These were <button onClick={navigate}>, which cost three separate
                  things: a crawler cannot follow them, so no internal link equity
                  reached /pricing, /docs, /integrations or /mcp from any page; a
                  visitor could not cmd-click, middle-click or right-click to open one
                  in a new tab, and got no URL preview on hover; and a screen-reader
                  user navigating by links found no navigation at all. `aria-current`
                  now carries the active state that the CSS class shows visually.
                */}
                <nav id="header-nav" aria-label={t(NavigationComponentMessages.MAIN_NAV_LABEL)} className={`header-nav${mobileOpen ? ' mobile-open' : ''}${isAuth ? ' header-nav-auth' : ''}`}>
                    {navLinks.map(link => (
                        <Link
                            key={link.path}
                            to={localePrefixedPath(link.path, location.pathname)}
                            className={`header-nav-link${isActive(link.path)}`}
                            aria-current={isActive(link.path) ? 'page' : undefined}
                        >
                            {link.label}
                        </Link>
                    ))}
                    {/*
                      Sign in lives here on a narrow screen and in .header-right on a
                      wide one, rather than in both places at once — CSS shows exactly
                      one of the two. Below ~992px the theme toggle, locale picker,
                      Sign in and the Get started CTA together needed more room than
                      the viewport, and the CTA lost: at 390px it was cut off by 36px
                      of its 81px and rendered as "Get start", while Sign in wrapped
                      onto three lines inside a 56px header. Moving the secondary
                      action into the menu that already exists keeps the primary
                      conversion CTA whole, and gives the mobile panel the auth entry
                      point it was missing entirely.
                    */}
                    {!isAuth && (
                        <button
                            className="header-nav-link header-nav-signin"
                            onClick={openSignIn}
                        >
                            {t(CommonMessages.SIGN_IN)}
                        </button>
                    )}
                </nav>

                <div className="header-right">
                    {/*
                      Theme toggle. Both icons are always rendered and CSS picks one
                      off [data-theme] on <html>, rather than branching on the
                      `theme` state. The attribute is set before the first paint by
                      the inline script in public/index.html, so the right icon shows
                      immediately — and because the markup no longer varies by theme,
                      it cannot disagree with the prerendered HTML during hydration.
                    */}
                    <button
                        className="header-nav-link header-theme-toggle"
                        onClick={toggleTheme}
                        aria-label="Dark mode"
                        aria-pressed={hydrated ? theme === 'dark' : undefined}
                    >
                        <svg className="header-theme-icon header-theme-icon-sun" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="5"/><path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/></svg>
                        <svg className="header-theme-icon header-theme-icon-moon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z"/></svg>
                    </button>
                    {/* Language */}
                    <LocaleSelector onOpen={() => setMobileOpen(false)} />

                    {isAuth ? (
                        <div ref={userRef} style={{ position: 'relative' }}>
                            <button className="header-user-btn" onClick={() => setUserMenuOpen(!userMenuOpen)}>
                                <div className="header-user-avatar">
                                    {user.picture ? <img src={user.picture} alt="" referrerPolicy="no-referrer" /> : initials}
                                </div>
                                <span>{user.name?.split(' ')[0] || 'Account'}</span>
                            </button>
                            {userMenuOpen && (
                                <div className="header-dropdown">
                                    <div className="header-dropdown-info">{user.email}</div>
                                    <button className="header-dropdown-item" onClick={() => { navigate('/billing'); setUserMenuOpen(false); }}>{t(BillingMessages.PAGE_TITLE)}</button>
                                    <button className="header-dropdown-item" onClick={() => { signOut(); setUserMenuOpen(false); }}>
                                        {t(CommonMessages.SIGN_OUT)}
                                    </button>
                                </div>
                            )}
                        </div>
                    ) : (
                        <>
                            {/* The wide-screen twin of the in-menu Sign in above. */}
                            <button className="header-nav-link header-signin-wide" onClick={openSignIn}>{t(CommonMessages.SIGN_IN)}</button>
                            {/* A link for the same reasons as the nav: this is the most
                                shared URL on the site and the one a crawler most needs
                                to follow. */}
                            <Link to={localePrefixedPath('/get-started', location.pathname)} className="header-cta">
                                {t(NavigationComponentMessages.NAV_GET_STARTED)}
                            </Link>
                        </>
                    )}
                </div>
            </div>
        </header>
    );
};
